// Busca e filtro por origem na EAP do desembolso (design D7). A árvore é preguiçosa — carrega
// um nível por vez —, então filtrar só as linhas abertas não alcançaria todos os itens. Com
// busca ou filtro ativos, o detalhamento troca a árvore por uma lista plana servida pelo
// back-end (`GET .../disbursement/targets?levels=step,substep,service`, contrato C4), que
// ignora acento e caixa, filtra por `origin=rule|base` e devolve as contagens por opção.
//
// Cada resultado reaproveita o `EapRuleRow` (a regra vigente e as ações "Definir regra" /
// "Editar regra"); em modo apresentação a linha já não oferece escrita. A árvore não é
// desmontada enquanto a lista aparece: limpar a busca e o filtro a devolve como estava.
import { useEffect, useRef, useState } from "react";

import { fetchDisbursementTargets } from "../disbursementApi";
import { isPhysicalOriginKind } from "../coverage";
import { reais } from "../monthEdit";
import EapRuleRow from "./EapRuleRow";

export const EAP_SEARCH_LEVELS = Object.freeze(["step", "substep", "service"]);
export const EAP_SEARCH_PAGE_SIZE = 50;
export const SEARCH_DEBOUNCE_MS = 300;

export const EAP_FILTERS = Object.freeze({ all: "all", rule: "rule", base: "base" });

const LEVEL_TITLES = Object.freeze({
  step: "Etapa",
  substep: "Subetapa",
  service: "Serviço",
});

const NODE_PREFIX = "node:";

// A terceira opção leva o nome da origem: "Pelo físico-financeiro" quando ela é física.
const BASE_FILTER_LABELS = Object.freeze({
  erp_disbursement: "Pelo desembolso do ERP",
  budget: "Pelo orçamento",
});

export const baseFilterLabel = (originKind, originLabel = null) => {
  if (/^Previsto\b/.test(String(originLabel ?? ""))) return "Pelo previsto";
  return isPhysicalOriginKind(originKind) || !originKind
    ? "Pelo físico-financeiro"
    : BASE_FILTER_LABELS[originKind] ?? "Pela origem";
};

const countText = (value) => (Number.isSafeInteger(value) ? ` (${value.toLocaleString("pt-BR")})` : "");

/**
 * O caminho na EAP como texto. A rota devolve `path` como lista de ancestrais
 * (`[{ wbsCodeId, wbs, label }]`, contrato C4); um texto pronto também é aceito.
 */
export const pathText = (path) => {
  if (typeof path === "string") return path;
  if (!Array.isArray(path)) return "";
  return path
    .map((step) => [step?.wbs, step?.label].filter(Boolean).join(" ") || step?.wbsCodeId || "")
    .filter(Boolean)
    .join(" › ");
};

/** O resultado da rota no formato de linha da árvore que o `EapRuleRow` lê. */
const rowItemOf = (target) => ({
  wbs_code_id: String(target.key ?? "").startsWith(NODE_PREFIX)
    ? target.key.slice(NODE_PREFIX.length)
    : target.key,
  description: target.label,
});

// O estado da linha sem regra própria, quando ela herda a de um nível acima.
const inheritedText = (target) => (
  target.governance === "inherited" || (!target.ruleId && target.inheritedFrom)
    ? `Regra herdada de ${target.inheritedFrom?.label ?? "um nível acima"}`
    : null
);

// Um serviço governado só em parte por regras de insumo aparece nas duas opções do filtro.
const PARTIAL_TEXT = "Parcial: parte por regra de insumo, o resto segue a origem";

/** Abaixo disso a busca não dispara: uma letra casaria quase toda a EAP. */
export const SEARCH_MIN_LENGTH = 2;

const SearchIcon = () => (
  <svg
    className="ampli-eap-search__icon"
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 20 20"
    width="16"
    height="16"
  >
    <circle cx="8.5" cy="8.5" r="5.75" fill="none" stroke="currentColor" strokeWidth="1.75" />
    <path d="m13 13 4 4" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

/**
 * A busca, o filtro e as contagens: o que o detalhamento mostra no topo da EAP. `query` é
 * `{ search, filter }` e mora em quem usa, que decide se a árvore ou a lista aparece.
 *
 * O campo segue o padrão de busca da plataforma: ícone à esquerda, limpar à direita (e Esc),
 * dica do mínimo de caracteres, e o filtro à mesma altura do campo, na mesma linha quando
 * cabe e embaixo quando não cabe.
 */
export const EapSearchBar = ({ query, onChange, counts, originKind, originLabel = null }) => {
  const options = [
    { value: EAP_FILTERS.all, label: "Todos", count: counts?.all },
    { value: EAP_FILTERS.rule, label: "Por regra", count: counts?.rule },
    { value: EAP_FILTERS.base, label: baseFilterLabel(originKind, originLabel), count: counts?.base },
  ];
  const text = String(query.search ?? "");
  const tooShort = text.trim() !== "" && text.trim().length < SEARCH_MIN_LENGTH;
  const clearText = () => onChange({ ...query, search: "" });
  return (
    <div className="ampli-eap-search" role="search">
      <div className="ampli-eap-search__field">
        <label className="ampli-eap-search__label" htmlFor="ampli-eap-search-input">
          Buscar na EAP
        </label>
        <div className="ampli-eap-search__control">
          <SearchIcon />
          <input
            id="ampli-eap-search-input"
            type="search"
            className="ampli-eap-search__input"
            aria-label="Buscar item da EAP por código ou descrição"
            aria-describedby="ampli-eap-search-hint"
            placeholder="Código ou descrição — ex.: 22.001 ou elevador"
            autoComplete="off"
            spellCheck={false}
            value={text}
            onChange={(event) => onChange({ ...query, search: event.target.value })}
            onKeyDown={(event) => {
              if (event.key === "Escape" && text !== "") {
                event.preventDefault();
                clearText();
              }
            }}
          />
          {text !== "" ? (
            <button
              type="button"
              className="ampli-eap-search__clear"
              aria-label="Limpar o texto da busca"
              title="Limpar (Esc)"
              onClick={clearText}
            >
              ×
            </button>
          ) : null}
        </div>
        <span id="ampli-eap-search-hint" className="ampli-eap-search__hint">
          {tooShort
            ? `Digite ao menos ${SEARCH_MIN_LENGTH} caracteres.`
            : "Alcança todas as etapas, subetapas e serviços, mesmo os que não estão abertos. Ignora acento."}
        </span>
      </div>
      <div className="ampli-eap-search__field ampli-eap-search__field--filter">
        <span className="ampli-eap-search__label" aria-hidden="true">Mostrar</span>
        <div
          className="ampli-eap-search__filter"
          role="radiogroup"
          aria-label="Filtrar itens pela origem do desembolso"
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={query.filter === option.value}
              className={`ampli-eap-search__option${query.filter === option.value ? " is-active" : ""}`}
              onClick={() => onChange({ ...query, filter: option.value })}
            >
              {option.label}
              {countText(option.count) ? (
                <span className="ampli-eap-search__count">{countText(option.count)}</span>
              ) : null}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export const isSearchActive = (query) => {
  const search = String(query?.search ?? "").trim();
  return search.length >= SEARCH_MIN_LENGTH || (query?.filter ?? EAP_FILTERS.all) !== EAP_FILTERS.all;
};

/**
 * As contagens e, com busca ou filtro ativos, a lista plana. Uma resposta de uma consulta já
 * substituída nunca sobrescreve a atual.
 */
export const useEapSearch = (workId, query, { debounceMs = SEARCH_DEBOUNCE_MS } = {}) => {
  const [state, setState] = useState({ status: "idle", items: [], counts: null, total: 0, page: 0, key: null });
  const generation = useRef(0);
  const typed = String(query.search ?? "").trim();
  const search = typed.length >= SEARCH_MIN_LENGTH ? typed : "";
  const active = isSearchActive(query);
  // A resposta guarda a consulta que a pediu: durante o debounce a lista já está ativa, mas
  // o estado ainda é o da consulta anterior (as contagens); mostrá-lo seria um resultado
  // errado por um instante.
  const key = `${search}\u0000${query.filter}`;

  const load = async (page, append) => {
    const requestKey = key;
    generation.current += 1;
    const current = generation.current;
    setState((previous) => ({ ...previous, status: append ? "loading_more" : "loading" }));
    const answer = await fetchDisbursementTargets(workId, {
      levels: EAP_SEARCH_LEVELS,
      ...(search ? { search } : {}),
      ...(query.filter !== EAP_FILTERS.all ? { origin: query.filter } : {}),
      page,
      // Sem busca nem filtro só as contagens importam: uma página mínima.
      pageSize: active ? EAP_SEARCH_PAGE_SIZE : 1,
    });
    if (current !== generation.current) return;
    if (!answer || !Array.isArray(answer.items)) {
      setState((previous) => ({ ...previous, status: "error" }));
      return;
    }
    setState((previous) => ({
      status: "ready",
      items: append ? [...previous.items, ...answer.items] : answer.items,
      counts: answer.counts ?? previous.counts,
      total: Number.isSafeInteger(answer.total) ? answer.total : answer.items.length,
      page,
      key: requestKey,
    }));
  };

  useEffect(() => {
    if (!workId) return undefined;
    if (debounceMs <= 0) {
      load(0, false);
      return undefined;
    }
    const timer = setTimeout(() => load(0, false), debounceMs);
    return () => clearTimeout(timer);
  }, [workId, search, query.filter]);

  return {
    ...state,
    status: active && state.key !== key && state.status === "ready" ? "loading" : state.status,
    active,
    retry: () => load(0, false),
    loadMore: () => load(state.page + 1, true),
  };
};

export const EapSearchResults = ({ result, onClear }) => {
  if (result.status === "loading" || result.status === "idle") {
    return <p className="card-label" role="status">Buscando itens da EAP…</p>;
  }
  if (result.status === "error") {
    return (
      <p className="alert alert-error" role="alert">
        {"Não foi possível buscar os itens da EAP agora. "}
        <button type="button" className="button button-outline small" onClick={result.retry}>
          Tentar de novo
        </button>
      </p>
    );
  }
  if (result.items.length === 0) {
    return (
      <div className="empty-state" role="status" data-testid="eap-search-empty">
        <p>Nenhum item da EAP foi encontrado.</p>
        <button type="button" className="button button-outline small" onClick={onClear}>
          Limpar busca
        </button>
      </div>
    );
  }
  return (
    <div className="ampli-eap-search__results" data-testid="eap-search-results">
      <p className="ampli-eap-search__summary" role="status">
        {`${result.total.toLocaleString("pt-BR")} ${result.total === 1 ? "item encontrado" : "itens encontrados"}`}
      </p>
      <div className="projection-table">
        <div className="accordion">
          {result.items.map((target) => (
            <EapRuleRow
              key={target.key}
              item={rowItemOf(target)}
              type={target.level}
              typeTitle={LEVEL_TITLES[target.level] ?? "EAP"}
              months={[]}
              ChildAccordionComponent={null}
              flat
              eyebrow={`${pathText(target.path) ? `${pathText(target.path)} / ` : ""}${LEVEL_TITLES[target.level] ?? "EAP"}`}
              stateLabel={inheritedText(target)}
              details={(
                <>
                  <span className="accordion-label">{`Valor: ${reais(target.plannedCents)}`}</span>
                  {target.governance === "partial" ? (
                    <span className="ampli-eap-row__governance" data-testid="eap-search-partial">
                      {PARTIAL_TEXT}
                    </span>
                  ) : null}
                </>
              )}
            />
          ))}
        </div>
      </div>
      {result.items.length < result.total ? (
        <button
          type="button"
          className="button button-outline small"
          disabled={result.status === "loading_more"}
          onClick={result.loadMore}
        >
          {result.status === "loading_more" ? "Carregando…" : "Mostrar mais itens"}
        </button>
      ) : null}
    </div>
  );
};
